import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { formatCurrency } from '../utils';
import api from '../api';

interface TopProduct {
    productId: number;
    productName: string;
    quantity: number;
    totalValue: number;
    percentageOfTotal: number;
}

interface PeriodData {
    period: string;
    products: TopProduct[];
    totalValue: number;
}

const TopProductsReport = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<PeriodData[]>([]);
    const [chartData, setChartData] = useState<{ [key: string]: any }>({});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            console.log('Fetching top products data...');
            const response = await api.get<PeriodData[]>('/analytics/top-products');
            console.log('Top products data:', response.data);
            setData(response.data);
            createCharts(response.data);
        } catch (error) {
            console.error('Error loading top products data:', error);
        } finally {
            setLoading(false);
        }
    };

    const createCharts = (periodData: PeriodData[]) => {
        const charts: { [key: string]: any } = {};

        periodData.forEach(period => {
            charts[period.period] = {
                labels: period.products.map(p => p.productName),
                datasets: [
                    {
                        data: period.products.map(p => p.totalValue),
                        backgroundColor: [
                            '#42A5F5',
                            '#66BB6A',
                            '#FFA726',
                            '#26C6DA',
                            '#7E57C2',
                            '#EC407A',
                            '#AB47BC',
                            '#8D6E63',
                            '#5C6BC0',
                            '#EF5350'
                        ]
                    }
                ]
            };
        });

        setChartData(charts);
    };

    const valueTemplate = (rowData: TopProduct) => {
        return formatCurrency(rowData.totalValue);
    };

    const percentageTemplate = (rowData: TopProduct) => {
        return `${rowData.percentageOfTotal.toFixed(2)}%`;
    };

    const quantityTemplate = (rowData: TopProduct) => {
        return rowData.quantity.toLocaleString();
    };

    if (loading) {
        return (
            <div className="card">
                <div className="border-round border-1 surface-border p-4">
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <span className="ml-2">Loading report...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="card">
                <div className="border-round border-1 surface-border p-4">
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-info-circle" style={{ fontSize: '2rem' }}></i>
                        <span className="ml-2">No data available</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <h5>Top Products Report</h5>
                    <TabView>
                        {data.map((period, index) => (
                            <TabPanel key={period.period} header={period.period}>
                                <div className="grid">
                                    <div className="col-12 lg:col-6">
                                        <Card title="Top Products Distribution">
                                            <Chart type="pie" data={chartData[period.period]} />
                                        </Card>
                                    </div>
                                    <div className="col-12 lg:col-6">
                                        <Card title="Period Summary">
                                            <div className="text-xl mb-2">
                                                Total Value: {formatCurrency(period.totalValue)}
                                            </div>
                                            <div className="text-lg">
                                                Top 10 Products by Value
                                            </div>
                                        </Card>
                                    </div>
                                    <div className="col-12">
                                        <DataTable
                                            value={period.products}
                                            loading={loading}
                                            responsiveLayout="scroll"
                                            className="mt-3"
                                            emptyMessage="No products found"
                                            sortField="totalValue"
                                            sortOrder={-1}
                                        >
                                            <Column field="productName" header="Product" sortable />
                                            <Column 
                                                field="quantity" 
                                                header="Quantity" 
                                                sortable 
                                                body={quantityTemplate}
                                            />
                                            <Column 
                                                field="totalValue" 
                                                header="Total Value" 
                                                body={valueTemplate} 
                                                sortable 
                                            />
                                            <Column 
                                                field="percentageOfTotal" 
                                                header="% of Total" 
                                                body={percentageTemplate}
                                                sortable 
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </TabPanel>
                        ))}
                    </TabView>
                </div>
            </div>
        </div>
    );
};

export default TopProductsReport;
